@if (initialized) {
    <div class="flex flex-column gap-3">

        <!-- Namespace + function selection -->
        <p-card>
            <div class="grid">
                <div class="col-12 md:col-4">
                    <div class="flex flex-column gap-2">
                        <label class="font-semibold text-color">Namespace</label>
                        <p-select
                                [options]="namespaceOptions"
                                optionLabel="label"
                                optionValue="value"
                                [showClear]="true"
                                placeholder="Namespace auswählen"
                                appendTo="body"
                                styleClass="w-full"
                                [ngModel]="selectedNamespace"
                                (ngModelChange)="onNamespaceSelected($event)">
                        </p-select>
                    </div>
                </div>
                <div class="col-12 md:col-8">
                    <div class="flex flex-column gap-2">
                        <label class="font-semibold text-color">Funktion</label>
                        <p-select
                                [options]="functionOptions"
                                optionLabel="label"
                                optionValue="value"
                                [filter]="true"
                                [filterBy]="'label,value'"
                                [showClear]="true"
                                [disabled]="!selectedNamespace"
                                placeholder="Funktion auswählen"
                                appendTo="body"
                                styleClass="w-full"
                                [ngModel]="selectedFunctionName"
                                (ngModelChange)="onFunctionSelected($event)">
                        </p-select>
                        @if (activeDefinition) {
                            <div class="flex align-items-center gap-2 mt-1">
                                @if (activeDefinition.idempotent) {
                                    <p-tag value="Idempotent"
                                           severity="info"
                                           icon="fa-regular fa-rotate"></p-tag>
                                }
                                @if (activeDefinition.returnType) {
                                    <p-tag [value]="'Rückgabetyp: ' + activeDefinition.returnType"
                                           severity="secondary"></p-tag>
                                }
                                @if (activeDefinition.requiresPermission) {
                                    <p-tag value="Berechtigung erforderlich"
                                           severity="warn"
                                           icon="fa-regular fa-shield"></p-tag>
                                }
                            </div>
                            @if (activeDefinition.description) {
                                <small class="text-color-secondary">{{ activeDefinition.description }}</small>
                            }
                        }
                    </div>
                </div>
            </div>
        </p-card>

        <!-- Permission hint -->
        @if (activeDefinition && hasPermissionConfig && isFunctionBlocked) {
            <p-card>
                <div class="flex align-items-start gap-2 text-orange-500">
                    <i class="fa-regular fa-triangle-exclamation mt-1"></i>
                    <div>
                        <div class="font-semibold">Berechtigung fehlt</div>
                        <div class="text-color-secondary">
                            Der umgebende Flow erlaubt den Aufruf von
                            <strong>{{ selectedNamespace }}.{{ selectedFunctionName }}</strong>
                            nicht. Bitte ergänzen Sie die Funktion in
                            <code>permissionConfigJson.allowedFunctions["{{ selectedNamespace }}"]</code>.
                        </div>
                    </div>
                </div>
            </p-card>
        }

        <!-- Parameter editor -->
        @if (activeDefinition) {
            <p-card>
                <ng-template pTemplate="header">
                    <div class="px-3 pt-3 font-semibold text-color">Parameter</div>
                </ng-template>
                @if (parameterStates.length === 0) {
                    <div class="text-color-secondary">Diese Funktion benötigt keine Parameter.</div>
                } @else {
                    <div class="flex flex-column gap-3">
                        @for (state of parameterStates; track state.definition.name) {
                            <div class="grid align-items-start">
                                <div class="col-12 md:col-3">
                                    <div class="flex flex-column">
                                        <span class="font-semibold text-color">
                                            {{ state.definition.name }}
                                            @if (state.definition.required) {
                                                <span class="text-red-500">*</span>
                                            }
                                        </span>
                                        <small class="text-color-secondary">{{ state.definition.type }}</small>
                                        @if (state.definition.description) {
                                            <small class="text-color-secondary">{{ state.definition.description }}</small>
                                        }
                                    </div>
                                </div>
                                <div class="col-12 md:col-3">
                                    <p-select
                                            [options]="modeOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            appendTo="body"
                                            styleClass="w-full"
                                            [ngModel]="state.mode"
                                            (ngModelChange)="onParameterModeChange(state, $event)">
                                    </p-select>
                                </div>
                                <div class="col-12 md:col-6">
                                    @switch (state.mode) {
                                        @case ('literal') {
                                            @if (state.definition.allowedValues && state.definition.allowedValues.length > 0) {
                                                <p-select
                                                        [options]="state.definition.allowedValues"
                                                        appendTo="body"
                                                        styleClass="w-full"
                                                        placeholder="Wert auswählen"
                                                        [ngModel]="state.literalValue"
                                                        (ngModelChange)="onLiteralChange(state, $event)">
                                                </p-select>
                                            } @else {
                                                <input pInputText
                                                       class="w-full"
                                                       [placeholder]="'Literal-Wert (' + state.definition.type + ')'"
                                                       [ngModel]="state.literalValue"
                                                       (ngModelChange)="onLiteralChange(state, $event)"/>
                                            }
                                        }
                                        @case ('variable') {
                                            @if (variables && variables.length > 0) {
                                                <p-select
                                                        [options]="variables"
                                                        appendTo="body"
                                                        styleClass="w-full"
                                                        placeholder="Variable auswählen"
                                                        [filter]="true"
                                                        [ngModel]="state.variableName"
                                                        (ngModelChange)="onVariableChange(state, $event)">
                                                </p-select>
                                            } @else {
                                                <input pInputText
                                                       class="w-full"
                                                       placeholder="Variablen-Name (ohne $$)"
                                                       [ngModel]="state.variableName"
                                                       (ngModelChange)="onVariableChange(state, $event)"/>
                                            }
                                        }
                                    }
                                </div>
                            </div>
                        }
                    </div>
                }
            </p-card>
        }

        <!-- Export alias + on-error -->
        @if (activeDefinition) {
            <p-card>
                <ng-template pTemplate="header">
                    <div class="px-3 pt-3 font-semibold text-color">Ergebnis &amp; Fehlerverhalten</div>
                </ng-template>
                <div class="grid">
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Export-Alias (optional)</label>
                            <input pInputText
                                   class="w-full"
                                   placeholder="z.B. notificationId"
                                   [ngModel]="exportAlias"
                                   (ngModelChange)="onExportAliasChange($event)"/>
                            <small class="text-color-secondary">
                                Unter diesem Namen wird das Ergebnis im Flow-Kontext bereitgestellt.
                            </small>
                        </div>
                    </div>
                    <div class="col-12 md:col-6">
                        <div class="flex flex-column gap-2">
                            <label class="font-semibold text-color">Fehlerverhalten</label>
                            <p-select
                                    [options]="onErrorOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    appendTo="body"
                                    styleClass="w-full"
                                    [ngModel]="onError"
                                    (ngModelChange)="onErrorPolicyChange($event)">
                            </p-select>
                        </div>
                    </div>
                </div>
            </p-card>
        }

        <!-- Dry-run -->
        @if (activeDefinition) {
            <p-card>
                <ng-template pTemplate="header">
                    <div class="flex align-items-center justify-content-between px-3 pt-3">
                        <span class="font-semibold text-color">Dry-Run</span>
                        <p-button
                                icon="fa-regular fa-flask-vial"
                                label="Dry-Run ausführen"
                                [disabled]="!areMandatoryParametersFilled() || dryRunLoading"
                                (click)="runDryRun()">
                        </p-button>
                    </div>
                </ng-template>

                @if (referencedVariableNames.length > 0) {
                    <div class="flex flex-column gap-2 mb-3">
                        <span class="font-semibold text-color">Test-Werte für Variablen</span>
                        @for (varName of referencedVariableNames; track varName) {
                            <div class="grid align-items-center">
                                <div class="col-12 md:col-4">
                                    <span class="text-color">{{ varName }}</span>
                                </div>
                                <div class="col-12 md:col-8">
                                    <input pInputText
                                           class="w-full"
                                           [ngModel]="testVariableValues[varName]"
                                           (ngModelChange)="onTestVariableValueChange(varName, $event)"
                                           placeholder="Test-Wert"/>
                                </div>
                            </div>
                        }
                    </div>
                }

                @if (dryRunLoading) {
                    <div class="text-color-secondary">
                        <i class="fa-regular fa-spinner-third fa-spin"></i> Dry-Run wird ausgeführt…
                    </div>
                } @else if (dryRunError) {
                    <div class="text-red-500">
                        <i class="fa-regular fa-circle-exclamation"></i> {{ dryRunError }}
                    </div>
                } @else if (dryRunResult !== null && dryRunResult !== undefined) {
                    <div class="surface-100 border-round p-3">
                        <div class="flex align-items-center justify-content-between mb-1">
                            <span class="text-color-secondary">Ergebnis</span>
                            @if (dryRunDurationMs !== null) {
                                <small class="text-color-secondary">{{ dryRunDurationMs }} ms</small>
                            }
                        </div>
                        <pre class="m-0 text-color">{{ dryRunResult | json }}</pre>
                    </div>
                } @else if (!areMandatoryParametersFilled()) {
                    <div class="text-color-secondary">
                        <i class="fa-regular fa-circle-info"></i> Bitte füllen Sie alle Pflichtparameter aus.
                    </div>
                }
            </p-card>
        }
    </div>
}
